import { useForm, useFieldArray } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useMemo } from "react";
import moment from 'moment';
import { accountService } from "../../services/accountService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import { GetExpenseListData, GetExpenseEdit, SetExpenseListData, SetExpenseEdit } from "../../store/expensesAtom";
import { formatFormError } from "../../helpers/formartFormError";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const PlusIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);
const TrashIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const FileDocIcon = () => (
    <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
);
const UploadIcon = () => (
    <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const inputCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";

const FormField = ({ label, children, required, error }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MAX_FILE_MB = 2;
const emptyLine = () => ({ expense_account_id: '', amount: '', tax_inclusive_id: '2', tax_id: '', remark: '' });

const buildXmlString = (lines) => {
    const items = lines.map(l =>
        `<a><expense_account_id>${l.expense_account_id}</expense_account_id>` +
        `<amount>${l.amount}</amount>` +
        `<tax_inclusive_id>${l.tax_inclusive_id}</tax_inclusive_id>` +
        `<tax_id>${l.tax_id || ''}</tax_id>` +
        `<remark>${l.remark || ''}</remark></a>`
    ).join('');
    return `<root>${items}</root>`;
};

// ─── Multi-file upload ────────────────────────────────────────────────────────
const MultiFileUpload = ({ files, onAdd, onRemove }) => {
    const inputRef = useRef(null);

    const handleChange = (e) => {
        const selected = Array.from(e.target.files || []);
        const valid = selected.filter(f => {
            if (f.size / 1024 / 1024 > MAX_FILE_MB) {
                alert(`"${f.name}" exceeds ${MAX_FILE_MB}MB and was skipped.`);
                return false;
            }
            return true;
        });
        onAdd(valid);
        e.target.value = '';
    };

    return (
        <div className="space-y-2">
            <div
                onClick={() => inputRef.current?.click()}
                className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-blue-300 cursor-pointer transition"
            >
                <UploadIcon />
                <div>
                    <p className="text-xs font-medium text-gray-500">Click to add receipts</p>
                    <p className="text-xs text-gray-400">JPG, PNG, PDF — max {MAX_FILE_MB}MB</p>
                </div>
                <input ref={inputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleChange} />
            </div>

            {files.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                    {files.map((file, idx) => {
                        const isImage = file.type.startsWith('image/');
                        return (
                            <div key={idx} className="relative group rounded-lg border border-gray-200 bg-white overflow-hidden">
                                <div className="flex items-center justify-center h-16 bg-gray-50">
                                    {isImage
                                        ? <img src={URL.createObjectURL(file)} alt={file.name} className="h-full w-full object-cover" />
                                        : <FileDocIcon />
                                    }
                                </div>
                                <p className="text-[10px] text-gray-400 px-1.5 py-1 truncate">{file.name}</p>
                                <button
                                    type="button"
                                    onClick={() => onRemove(idx)}
                                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
            {files.length > 0 && (
                <p className="text-xs text-gray-400">{files.length} new file{files.length > 1 ? 's' : ''} selected</p>
            )}
        </div>
    );
};

// ─── Panel ────────────────────────────────────────────────────────────────────
export const CreateExpensePanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved, expenseAccountsList = [] }) => {
    const [saving, setSaving] = useState(false);
    const [newFiles, setNewFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const expenseDataStore = GetExpenseListData();
    const editableExp = GetExpenseEdit();
    const setExpenseListData = SetExpenseListData();
    const setExpenseEdit = SetExpenseEdit();

    const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
        defaultValues: {
            expense_date: moment().format('YYYY-MM-DD'),
            vendor_id: '',
            expense_reference: '',
            expense_remark: '',
            lines: [emptyLine()],
        }
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
    const watchedLines = watch('lines');

    // Load dropdown data
    useEffect(() => {
        if (!expenseDataStore?.paid_account?.length) {
            getExpensesListFromApi();
        }
    }, [expenseDataStore?.paid_account?.length]);

    // Populate form on edit
    useEffect(() => {
        if (editableExp?.Ref_No && isOpen) {
            const mappedLines = editableExp.lines?.length
                ? editableExp.lines.map(line => ({
                    expense_account_id: String(line.Expense_Account_Id || ''),
                    amount: line.Amount || '',
                    tax_inclusive_id: String(line.Tax_Inclusive_Id || '2'),
                    tax_id: line.Tax_Id || '',
                    remark: line.Expense_Notes || '',
                }))
                : [emptyLine()];

            reset({
                expense_date: moment(editableExp.Expense_Date).format('YYYY-MM-DD'),
                vendor_id: editableExp.Vendor_Id ? String(editableExp.Vendor_Id) : '',
                expense_remark: editableExp.Expense_Remark || '',
                expense_reference: editableExp.Expense_Reference || '',
                lines: mappedLines,
            });

            setExistingAttachments(editableExp.attachmentUrls || []);
            setNewFiles([]);
        }
    }, [editableExp, isOpen]);

    // Reset when opening fresh or closing
    useEffect(() => {
        if (isOpen && !editableExp?.Ref_No) {
            reset({
                expense_date: moment().format('YYYY-MM-DD'),
                vendor_id: '',
                expense_reference: '',
                expense_remark: '',
                lines: [emptyLine()],
            });
            setNewFiles([]);
            setExistingAttachments([]);
        } else if (!isOpen) {
            reset({
                expense_date: moment().format('YYYY-MM-DD'),
                vendor_id: '',
                expense_reference: '',
                expense_remark: '',
                lines: [emptyLine()],
            });
            setNewFiles([]);
            setExistingAttachments([]);
        }
    }, [isOpen]);

    const getExpensesListFromApi = () => {
        accountService.getExpenseMainList().then(response => {
            const serverData = response?.data;
            setExpenseListData({
                vendor_info: serverData?.vendor_info || [],
                expense_account: serverData?.expense_account || [],
                expense_info: serverData?.expense_info || [],
                expense_status: serverData?.expense_status || [],
                paid_account: serverData?.paid_account || [],
                tax_info: serverData?.tax_info || [],
            });
        });
    };

    const onErrors = (error) => {
        logicContext.showToast(formatFormError(error), 'error');
    };

    const onSubmit = async (data) => {
        const result = await myPromiseModal({
            title: 'Confirmation',
            body: editableExp?.Ref_No ? 'Do you want to update this record?' : 'Do you want to save this record?',
            showInput: false,
            placeHolder: '',
        });

        if (!result?.decision) return;

        setSaving(true);
        try {
            const option = editableExp?.Ref_No ? 2 : 1;
            const formData = new FormData();
            formData.append('option', option);
            if (editableExp?.Ref_No) formData.append('ref_no', editableExp.Ref_No);
            formData.append('expense_date', data.expense_date);
            formData.append('paid_account_id', accountId);
            formData.append('vendor_id', data.vendor_id || '');
            formData.append('expense_remark', data.expense_remark || '');
            formData.append('expense_reference', data.expense_reference || '');
            formData.append('xml_string', buildXmlString(data.lines));
            newFiles.forEach(file => formData.append('files[]', file));
            const existingUrlsCsv = existingAttachments.map(a => a.Attachment_URL).join(',');
            if (existingUrlsCsv) formData.append('existing_img_urls', existingUrlsCsv);

            const res = await accountService.submitExpense(formData);
            if (res?.data !== undefined) {
                const { message_text, message_id } = res.data[0] || res.data?.result_status[0];
                if (message_id === 1) {
                    logicContext.showToast(message_text, 'success');
                    setExpenseEdit({});
                    onSaved?.();
                    onClose();
                } else {
                    logicContext.showToast(message_text, 'error');
                }
            }
        } catch (err) {
            console.error("Failed to save expense", err);
            logicContext.showToast("Failed to save expense", 'error');
        } finally {
            setSaving(false);
        }
    };

    const groupedAccountOptions = useMemo(() => {
        const groups = {};
        expenseAccountsList.forEach(acc => {
            const key = acc.parent_account_id;
            if (!groups[key]) groups[key] = { label: acc.parent_account_name || String(key), accounts: [] };
            groups[key].accounts.push(acc);
        });
        return Object.values(groups);
    }, [expenseAccountsList]);

    return (
        <div
            className={`fixed top-0 right-0 h-screen w-[480px] bg-white z-50 flex flex-col overflow-hidden
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button onClick={onBack} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition">
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                            {editableExp?.Ref_No ? 'Edit' : 'New'} Expense
                        </p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || 'Add Expense'}
                        </h3>
                    </div>
                </div>
                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition">
                    <XIcon />
                </button>
            </div>

            {/* ── Bank pill ── */}
            {bankName && (
                <div className="px-5 pt-3 pb-1 flex-shrink-0">
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-xs text-blue-700 font-medium">
                            From: <span className="font-bold">{bankName}</span>
                        </span>
                    </div>
                </div>
            )}

            {/* ── Scrollable form body ── */}
            <form onSubmit={handleSubmit(onSubmit, onErrors)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Date */}
                <FormField label="Date" required error={errors.expense_date?.message}>
                    <input
                        type="date"
                        className={inputCls}
                        {...register('expense_date', { required: 'Date is required' })}
                    />
                </FormField>

                {/* Vendor */}
                <FormField label="Vendor">
                    <select className={selectCls} {...register('vendor_id')}>
                        <option value="">Select vendor</option>
                        {expenseDataStore?.vendor_info?.map((v, idx) => (
                            <option key={idx} value={v.Vendor_Id}>{v.Vendor_Name}</option>
                        ))}
                    </select>
                </FormField>

                {/* Reference */}
                <FormField label="Reference #">
                    <input type="text" maxLength={500} placeholder="Max. 500 characters" className={inputCls} {...register('expense_reference')} />
                </FormField>

                {/* Note */}
                <FormField label="Note">
                    <textarea rows={2} maxLength={500} placeholder="Max. 500 characters" className={`${inputCls} resize-none`} {...register('expense_remark')} />
                </FormField>

                {/* ── Expense Lines ── */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Expense Lines</span>
                        <button
                            type="button"
                            onClick={() => append(emptyLine())}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                        >
                            <PlusIcon /> Add Line
                        </button>
                    </div>

                    <div className="space-y-3">
                        {fields.map((field, index) => {
                            const rowTaxType = watchedLines?.[index]?.tax_inclusive_id;
                            return (
                                <div key={field.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100 space-y-2.5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-400">Line {index + 1}</span>
                                        <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            disabled={fields.length === 1}
                                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>

                                    {/* Expense Account */}
                                    <FormField label="Expense Account" required error={errors.lines?.[index]?.expense_account_id?.message}>
                                        <div className="relative">
                                            <select
                                                className={selectCls}
                                                {...register(`lines.${index}.expense_account_id`, { required: 'Required' })}
                                            >
                                                <option value="">Select account</option>
                                                {groupedAccountOptions.map(group => (
                                                    <optgroup key={group.label} label={group.label}>
                                                        {group.accounts.map(acc => (
                                                            <option key={acc.account_id} value={acc.account_id}>
                                                                {acc.account_name}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </FormField>

                                    {/* Amount + Tax Type side by side */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <FormField label="Amount" required error={errors.lines?.[index]?.amount?.message}>
                                            <div className="flex">
                                                <span className="inline-flex items-center px-2 border border-r-0 border-gray-200 rounded-l-lg bg-gray-100 text-xs font-medium text-gray-500">NGN</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    className={`${inputCls} rounded-l-none`}
                                                    {...register(`lines.${index}.amount`, {
                                                        required: 'Required',
                                                        validate: v => parseFloat(v) > 0 || 'Must be > 0',
                                                    })}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="Tax Type">
                                            <select className={selectCls} {...register(`lines.${index}.tax_inclusive_id`)}>
                                                <option value="2">Tax Exclusive</option>
                                                <option value="1">Tax Inclusive</option>
                                            </select>
                                        </FormField>
                                    </div>

                                    {/* Tax dropdown (conditional) */}
                                    {rowTaxType === '1' && (
                                        <FormField label="Tax">
                                            <div className="relative">
                                                <select className={selectCls} {...register(`lines.${index}.tax_id`)}>
                                                    <option value="">Select tax</option>
                                                    {expenseDataStore?.tax_info?.map((tax, idx) => (
                                                        <option key={idx} value={tax.Tax_Id}>{tax.Tax_Name}</option>
                                                    ))}
                                                </select>
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                </div>
                                            </div>
                                        </FormField>
                                    )}

                                    {/* Line remark */}
                                    <FormField label="Line Note">
                                        <input type="text" placeholder="Optional…" maxLength={500} className={inputCls} {...register(`lines.${index}.remark`)} />
                                    </FormField>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Attachments ── */}
                <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Attach Receipts</span>

                    {/* Existing uploads (edit mode) */}
                    {existingAttachments.length > 0 && (
                        <div className="mb-2">
                            <p className="text-xs text-gray-400 mb-1.5">Already uploaded — hover to remove:</p>
                            <div className="grid grid-cols-3 gap-2">
                                {existingAttachments.map((att, idx) => {
                                    const url = att.Attachment_URL;
                                    const isImage = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url);
                                    return (
                                        <div key={idx} className="relative group rounded-lg border border-blue-200 bg-blue-50 overflow-hidden">
                                            <div className="flex items-center justify-center h-16 bg-gray-50">
                                                {isImage
                                                    ? <img src={url} alt="" className="h-full w-full object-cover" />
                                                    : <FileDocIcon />
                                                }
                                            </div>
                                            <p className="text-[10px] text-gray-400 px-1.5 py-1 truncate">{url?.split('/').pop()}</p>
                                            <button
                                                type="button"
                                                onClick={() => setExistingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <MultiFileUpload
                        files={newFiles}
                        onAdd={incoming =>
                            setNewFiles(prev => {
                                const seen = new Set(prev.map(f => f.name + f.size));
                                return [...prev, ...incoming.filter(f => !seen.has(f.name + f.size))];
                            })
                        }
                        onRemove={idx => setNewFiles(prev => prev.filter((_, i) => i !== idx))}
                    />
                </div>

                <div className="h-2" />
            </form>

            {/* ── Footer ── */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                    type="button"
                    onClick={handleSubmit(onSubmit, onErrors)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : editableExp?.Ref_No ? 'Update Expense' : 'Save Expense'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};
